import PropTypes from 'prop-types';
import React from 'react';
import entityComposition from '../../common/classes/entityComposition';
import { calculate } from '../../../../behaviors/src/behaviors/formulaBehavior';
import { unitsSelector, valueSelector, placeholderSelector, newPathSelector, enabledSelector, labelSelector, conformanceSelector } from '../selectors/index';
import { UncomposedNumberField } from './numberField';
import logValueFunc from '../commonActions/logValue';
import onChangeFunc from '../commonActions/onChange';
import localChangeSelector from '../selectors/localChangeSelector';
const UnitsField = ({
  unitsTypes,
  onChange,
  notificationActions,
  enabled,
  path,
  logValue,
  operationComplete,
  operationPending,
  localChangeFunc,
  value: _value = undefined,
  maximumFractionDigits: _maximumFractionDigits = 2,
  minimumIntegerDigits: _minimumIntegerDigits = 0,
  minimumFractionDigits: _minimumFractionDigits = 0,
  placeholder: _placeholder = '',
  displayUnit: _displayUnit = '',
  conformanceText: _conformanceText = null,
  label: _label = null,
  testingIdentifier: _testingIdentifier = 'default-units-field'
}) => {
  const storageUnitsIdentifier = Object.keys(unitsTypes).find(key => !unitsTypes[key].toDefault);
  const {
    label: currentUnitlabel,
    fromDefault,
    toDefault
  } = unitsTypes[_displayUnit] || storageUnitsIdentifier;
  const convertValue = (formula, variables) => {
    try {
      const result = calculate(formula, variables);
      return result;
    } catch (message) {
      // id ensures only one popup at a time
      notificationActions.error({
        message,
        id: 'expression_parse_error'
      });
    }
    return null;
  };
  const getDisplayValue = currentValue => {
    if (currentValue === undefined) {
      return undefined;
    }
    if (storageUnitsIdentifier === _displayUnit) {
      return _value;
    }
    // console.log(fromDefault, currentValue);
    const convertedValue = convertValue(fromDefault, {
      value: currentValue
    });
    const rawValue = convertedValue !== null ? convertedValue : currentValue;
    const fixedValue = (rawValue || 0) === 0 ? rawValue : rawValue.toFixed(_maximumFractionDigits);
    // Forcing to a number will turn 12.00 into just plain 12 or 0.0 into 0 for cleaner display
    // but it will do nothing to precision: i.e. 12.32 stays as 12.32
    // console.log(convertedValue, currentValue);
    return fixedValue ? Number(fixedValue) : fixedValue;
  };
  const getSaveValue = currentValue => {
    if (currentValue === null) {
      return null;
    }
    if (storageUnitsIdentifier === _displayUnit) {
      return currentValue;
    }
    const convertedValue = convertValue(toDefault, {
      value: currentValue
    });
    return convertedValue !== null ? convertedValue : currentValue;
  };
  const displayValue = getDisplayValue(_value);
  const handleChange = async ({
    path: inPath,
    value: changeValue,
    changeModifiers: _changeModifiers = {}
  }) => {
    await operationPending();
    const saveValue = getSaveValue(changeValue);
    const log = {
      value: saveValue,
      displayValue: `${changeValue} ${_displayUnit || ''}`
    };
    await onChange({
      path: inPath,
      value: saveValue,
      changeModifiers: _changeModifiers
    });

    // log the display value.
    await logValue(log);
    return operationComplete();
  };
  return /*#__PURE__*/React.createElement(UncomposedNumberField, {
    conformanceText: _conformanceText,
    enabled: enabled,
    displayUnit: _displayUnit,
    label: _label,
    maximumFractionDigits: _maximumFractionDigits,
    minimumFractionDigits: _minimumFractionDigits,
    minimumIntegerDigits: _minimumIntegerDigits,
    operationPending: operationPending
    // we send a no-op for operation complete, because
    // we call it in this class level instead when we are all done.
    ,
    operationComplete: () => {},
    testingIdentifier: _testingIdentifier,
    onChange: handleChange,
    value: displayValue,
    internalLabel: currentUnitlabel,
    placeholder: _placeholder,
    path: path
    // we want to log the value in here instead of
    // in the numberField, so we pass a no-op for
    // this function.
    ,
    logValue: () => {},
    localChangeFunc: localChangeFunc
  });
};
UnitsField.propTypes = {
  conformanceText: PropTypes.string,
  path: PropTypes.string.isRequired,
  displayUnit: PropTypes.string,
  testingIdentifier: PropTypes.string,
  label: PropTypes.string,
  enabled: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  logValue: PropTypes.func.isRequired,
  operationPending: PropTypes.func.isRequired,
  localChangeFunc: PropTypes.func.isRequired,
  operationComplete: PropTypes.func.isRequired,
  maximumFractionDigits: PropTypes.number,
  minimumIntegerDigits: PropTypes.number,
  minimumFractionDigits: PropTypes.number,
  placeholder: PropTypes.string,
  unitsTypes: PropTypes.shape({}).isRequired,
  format: PropTypes.shape({}).isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  notificationActions: PropTypes.shape({
    error: PropTypes.func
  }).isRequired
};
export { UnitsField as UncomposedUnitsField };
export default entityComposition(UnitsField, [], {
  conformanceText: conformanceSelector,
  displayUnit: unitsSelector,
  value: valueSelector,
  placeholder: placeholderSelector,
  path: newPathSelector,
  enabled: enabledSelector,
  label: labelSelector,
  logValue: logValueFunc,
  onChange: onChangeFunc,
  localChangeFunc: localChangeSelector
}, ['notification']);